<template>
  <v-container fluid class="pa-0" id="step21">
    <v-card>
      <v-card-actions class="card-bg-2">
        <v-btn color="info" @click="generate()">
          <v-icon class="mr-1">insert_chart</v-icon>
          <div class="text-capitalize">
            {{ $t('Button.Generat_Graph_Dashboard') }}
          </div>
        </v-btn>
      </v-card-actions>

      <div style="background: white;">
        <v-alert :value="!hasWaterMediaType" type="warning" outline class="ma-0">
          <div class="primary--text">
            Sorry, graphs are only available for water samples.
          </div>
        </v-alert>
      </div>

      <v-divider />
      <v-layout class="card-bg-2">
        <v-flex xs12 md5 class="divider-right">
          <v-card-title class="headline pb-0">Media Types</v-card-title>
          <v-card-text class="py-0">
            <v-radio-group v-model="selectedMedia" row>
              <template v-for="m in analyteMediaTypes">
                <v-radio color="info" :key="m" :label="m" :value="m"></v-radio>
              </template>
            </v-radio-group>
          </v-card-text>
          <v-divider />
          <v-card-title class="headline">Date Range</v-card-title>
          <v-card-text class="py-0">
            <DateRange v-model="dates" rounded solo :minDate="minDate" :maxDate="maxDate" :availableDateRange="{ start: minDate, end: maxDate }" :min-max-placeholder="formats.prettyDateFormat" :shortcutPrefix="'Show'" :shortcuts="shortcuts" no-arrows :data="availableDates" />
          </v-card-text>
          <v-divider />
        </v-flex>
        <v-flex xs12 md5 class="divider-left">
          <WaterQualityGuideline v-if="hasWaterMediaType" @onGuidelineSelect="onGuidelineSelectHandler" :selectedMedia="selectedMedia" :selectedGuidelines="selectedGuidelines" :preConfig="preConfig"/>
        </v-flex>
      </v-layout>
    </v-card>

    <StationWaterQualityDashChart :graphRequestData="graphRequestData" v-if="hasWaterMediaType" />

  </v-container>
</template>

<script>
  import moment from 'moment'

  import {
    createNamespacedHelpers,
  } from 'vuex'

  const monitoringFeatures = createNamespacedHelpers('monitoringfeature')
  const dataSources = createNamespacedHelpers('datasource')

  import {
    SOURCE_TYPES,
  } from '@/store/MonitoringFeature/monitoringfeature'

  import DataTable from '@/components/mixins/DataTable'
  import {
    min,
    max,
  } from 'date-fns'

  import StationWaterQualityDashChart from './StationWaterQualityDashChart'
  import WaterQualityGuideline from './WaterQualityGuideline'

  import {
    prettyDateFormat,
  } from '@/components/mixins/MomentUtil'

  export default {
    components: {
      StationWaterQualityDashChart,
      WaterQualityGuideline,
    },
    mixins: [DataTable],
    data() {
      return {
        initialized: false,
        search: '',
        formats: {
          prettyDateFormat,
        },
        pagination: {
          sortBy: 'Analyte',
          rowsPerPage: 10,
        },
        headers: [{
          text: 'Analyte Name',
          value: 'Analyte',
        },
        {
          text: 'First Record',
          value: 'FirstRecordDate',
        },
        {
          text: 'Last Record',
          value: 'LastRecordDate',
        },
        {
          text: '# Records',
          value: 'NumberOfRecords',
        },
        ],
        includeQaQc: false,
        selectedGuidelines: [],
        graphRequestData: null,
        // For the highlighting and disabling of future daterangepicker feature
        availableDates: [],
        dates: {
          start: null,
          end: null,
        },
        minDate: null, // Date object
        maxDate: null, // Date object
        showErrorBox: false,
        selectedMedia: 'Water',
        onDateRangeSelectedFlag: false,
        tabClickedOnce: false,
        isGraphsVisible: false,
        preConfig: null,
      }
    },
    computed: {
      ...monitoringFeatures.mapGetters([
        'currentFeatureDataSourceIndexByType',
        'currentFeatureDataSourceData',
      ]),
      ...dataSources.mapGetters([
        'dataSourceIndex',
      ]),
      featureDataSource() {
        return this.currentFeatureDataSourceIndexByType[SOURCE_TYPES.discreteEnvirodata]
      },
      discreteStation() {
        return this.dataSourceIndex[SOURCE_TYPES.discreteEnvirodata][this.featureDataSource.SourceId]
      },
      stationName() {
        return this.discreteStation.LocationIdentifier
      },
      discreteData() {
        return this.currentFeatureDataSourceData(SOURCE_TYPES.discreteEnvirodata)
      },
      waterQualityData() {
        return (this.discreteData && this.discreteData.wqData) || {}
      },
      datasets() {
        return (this.waterQualityData && this.waterQualityData.WaterQualityDatasets) || []
      },
      shortcuts() {
        return {
          all: {
            name: () => 'All available data',
            make: () => {
              return {
                start: moment(this.minDate),
                end: moment(this.maxDate),
              }
            },
          },
        }
      },
      analyteMediaTypes() {
        let analytes = new Set()
        this.datasets.forEach(ds => analytes.add(ds.MediaType))
        return [...analytes]
      },
      hasWaterMediaType() {
        let matched = this.analyteMediaTypes.find(x => x.indexOf('Water') >= 0)
        return matched
      },
    },
    watch: {
      selectedMedia: {
        immediate: true,
        handler() {
          let data = this.datasets.filter(x => x.MediaType == this.selectedMedia)
          let startDates = null
          let endDates = null
          let availableDates = []

          if (this.selected.length > 0) {
            startDates = this.selected.map(d => d.firstRecordDate)
            endDates = this.selected.map(d => d.lastRecordDate)
            availableDates = startDates.concat(endDates)

          } else {
            startDates = data.map(d => d.FirstRecordDate)
            endDates = data.map(d => d.LastRecordDate)
          }

          this.availableDates = availableDates
          this.minDate = min(...startDates)
          this.maxDate = max(...endDates)

          this.dates = {
            start: moment(this.maxDate).add(-3, 'month').toDate(),
            end: this.maxDate,
          }
        },
      },
      discreteStation: {
        immediate: true,
        async handler() {          
          if(this.discreteStation) {
            let preConfigData = await this.getStationDashboardPreConfig({stationId: this.discreteStation.Id})
            if(preConfigData) {
              this.preConfig = preConfigData              
            }
          }
        },
      },
    },
    methods: {
      ...monitoringFeatures.mapActions([
        'getStationDashboardPreConfig',
      ]),
      /* Fix later */
      setJDSDefault() {
        let defaultGuideline = [{
          Id: 7,
          GuidelineName: 'JDS_WTF_MAXGRAB',
          GuidelineLongName: 'JDS Discharge from WTF, Max Authorized Concentration in a Grab Sample',
        }]

        this.selectedGuidelines = defaultGuideline
        this.generate()
      },
      // initDates() {
      //   if (!this.datasets || this.datasets.length == 0) return

      //   let datasets = this.datasets.filter(x => x.MediaType == this.selectedMedia)

      //   let timeRange = getDataTimeRange(datasets)
      //   this.minDateBound = timeRange[0]
      //   this.maxDateBound = timeRange[1]

      //   this.dates = {
      //     start: moment(this.maxDate).add(3, 'month').toDate(),
      //     end: this.maxDate,
      //   }
      // },
      generate() {
        this.showErrorBox = false
        this.isGraphsVisible = true

        // if (!this.tabClickedOnce) {
        //   this.initDates()
        // }

        this.graphRequestData = {
          stationName: this.stationName,
          stationId: this.discreteStation.Id,
          startDate: this.dates.start,
          endDate: this.dates.end,
          guidelines: this.selectedGuidelines,
          datasets: this.selected,
          media: this.selectedMedia,
          includeQaQc: this.includeQaQc,
        }
      },
      onGuidelineSelectHandler(data) {
        // Trigger overwrites JDS default otherwise
        // if (!this.initialized) {
        //   this.initialized = true
        //   return
        // }

        this.selectedGuidelines = data ? [data] : []
      },
    },
    mounted() {      
      //this.setJDSDefault()
    },
  }
</script>